import React, { useEffect, useState } from 'react';
import { manuscriptsAPI, reviewsAPI, reviewersAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Clock, CheckCircle, XCircle, AlertCircle, Download, UserPlus, Send, Filter, Search, Layers, X, BarChart3, Loader2, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  'En Revision': { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
  'Revisiones Requeridas': { color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
  'Aceptado': { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
  'Rechazado': { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  'Publicado': { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Layers },
};

export default function ManuscriptsList({ filterStatus }) {
  const [manuscripts, setManuscripts] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const isDictaminados = filterStatus === 'dictaminados';
  const [statusFilter, setStatusFilter] = useState(isDictaminados ? 'all' : (filterStatus || 'all'));
  const [selectedMs, setSelectedMs] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewForm, setReviewForm] = useState({ decision: '', comentarios: '', comentarios_privados: '' });
  const [assignReviewerId, setAssignReviewerId] = useState('');
  const { isAdmin, isEditor } = useAuth();
  const isEditorial = isAdmin() || isEditor();
  const [downloading, setDownloading] = useState(null);
  const [deadlineDialog, setDeadlineDialog] = useState(null);
  const [deadlineDate, setDeadlineDate] = useState('');

  const handleDownload = async (manuscript) => {
    setDownloading(manuscript.id);
    try {
      await manuscriptsAPI.downloadBlob(manuscript.id);
      toast.success('Descarga iniciada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descargar el manuscrito. Intenta de nuevo.');
    } finally {
      setDownloading(null);
    }
  };

  const handleSetDeadline = async () => {
    if (!deadlineDialog || !deadlineDate) return;
    try {
      const isoDate = new Date(deadlineDate + 'T23:59:59Z').toISOString();
      await manuscriptsAPI.setDeadline(deadlineDialog.id, isoDate);
      setDeadlineDialog(null);
      setDeadlineDate('');
      toast.success('Fecha limite establecida');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al establecer fecha limite');
    }
  };

  const loadData = () => {
    Promise.all([
      manuscriptsAPI.list().then(r => setManuscripts(r.data.items || r.data)),
      (isAdmin() || isEditor()) ? reviewersAPI.list().then(r => setReviewers(r.data)) : Promise.resolve(),
    ]).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const dictaminadosStatuses = ['Aceptado', 'Rechazado', 'Revisiones Requeridas'];
  const filtered = manuscripts.filter(m => {
    if (isDictaminados) {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (statusFilter === 'all' && !dictaminadosStatuses.includes(m.status)) return false;
    } else {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
    }
    if (search && !m.titulo.toLowerCase().includes(search.toLowerCase()) && !m.autor_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleAssign = async () => {
    if (!assignDialog || !assignReviewerId) return;
    try {
      await manuscriptsAPI.assignReviewer(assignDialog.id, assignReviewerId);
      setAssignReviewerId('');
      setAssignDialog(null);
      toast.success('Revisore asignade exitosamente');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al asignar revisore'); }
  };

  const handleRemoveReviewer = async (msId, reviewerId) => {
    if (!window.confirm('Remover a este revisore?')) return;
    try {
      await manuscriptsAPI.removeReviewer(msId, reviewerId);
      toast.success('Revisore removide');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al remover revisore'); }
  };

  const handleReview = async () => {
    if (!reviewDialog || !reviewForm.decision || !reviewForm.comentarios.trim()) return;
    try {
      const res = await reviewsAPI.create({
        manuscript_id: reviewDialog.id,
        decision: reviewForm.decision,
        comentarios: reviewForm.comentarios,
        comentarios_privados: reviewForm.comentarios_privados,
      });
      setReviewDialog(null);
      setReviewForm({ decision: '', comentarios: '', comentarios_privados: '' });
      toast.success(isEditorial ? `Decision editorial aplicada: ${res.data?.new_status}` : 'Dictamen enviado exitosamente');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar dictamen'); }
  };

  const titles = {
    'all': isDictaminados ? 'Manuscritos Dictaminados' : 'Todos los Manuscritos',
    'En Revision': 'Envios en Revision',
    'Revisiones Requeridas': 'Revisiones Requeridas',
    'Aceptado': 'Manuscritos Aceptados',
    'Rechazado': 'Manuscritos Rechazados',
    'Publicado': 'Manuscritos Publicados',
  };

  const filterOptions = isDictaminados
    ? ['all', 'Aceptado', 'Revisiones Requeridas', 'Rechazado']
    : ['all', 'En Revision', 'Revisiones Requeridas', 'Aceptado', 'Rechazado', 'Publicado'];

  // Get reviewers for a manuscript (supports both old single-reviewer and new multi-reviewer format)
  const getReviewers = (m) => {
    if (m.reviewers && m.reviewers.length > 0) return m.reviewers;
    if (m.reviewer_id && m.reviewer_name) return [{ id: m.reviewer_id, name: m.reviewer_name }];
    return [];
  };

  // Available reviewers for assignment (exclude already assigned)
  const getAvailableReviewers = (m) => {
    const assigned = getReviewers(m).map(r => r.id);
    return reviewers.filter(r => !assigned.includes(r.id));
  };

  if (loading) return <div className="flex-1 flex justify-center items-center"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto" data-testid="manuscripts-list">
      <div className="max-w-5xl">
        <h1 className="text-2xl font-serif font-bold text-slate-dark mb-2">{titles[statusFilter] || 'Manuscritos'}</h1>
        <p className="text-sm font-sans text-gray-400 mb-6">{filtered.length} manuscrito{filtered.length !== 1 ? 's' : ''}</p>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.5} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por titulo o autore..." className="pl-9 font-sans text-sm" data-testid="search-manuscripts" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {filterOptions.map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-sans rounded-md border transition-colors ${statusFilter === s ? 'bg-slate-dark text-white border-slate-dark' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
                data-testid={`filter-${s}`}
              >
                {s === 'all' ? 'Todos' : s}
              </button>
            ))}
          </div>
        </div>

        {/* Manuscripts */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-sm border border-gray-100">
            <Filter className="w-10 h-10 text-gray-200 mx-auto mb-3" strokeWidth={1} />
            <p className="font-sans text-gray-400">No hay manuscritos con estos filtros.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-sm divide-y divide-gray-50">
            {filtered.map((m, i) => {
              const sc = statusConfig[m.status] || statusConfig['En Revision'];
              const Icon = sc.icon;
              const msReviewers = getReviewers(m);
              return (
                <div key={m.id} className="p-5 hover:bg-gray-50/30 transition-colors" data-testid={`manuscript-row-${i}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${sc.color.split(' ').slice(0,1)} border ${sc.color.split(' ').slice(2).join(' ')}`}>
                      <Icon className={`w-4 h-4 ${sc.color.split(' ')[1]}`} strokeWidth={1.5} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] font-sans ${sc.color}`}>{m.status}</Badge>
                        {m.categoria && <span className="text-[10px] font-sans text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{m.categoria}</span>}
                      </div>
                      <h3 className="font-serif font-semibold text-slate-dark leading-snug cursor-pointer hover:text-trans-blue transition-colors" onClick={() => setSelectedMs(m)}>
                        {m.titulo}
                      </h3>
                      <p className="text-xs font-sans text-gray-400 mt-1">
                        <span className="font-medium">{m.autor_name}</span> &middot; {m.autor_email} &middot; {new Date(m.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      {/* Reviewers list */}
                      {msReviewers.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {msReviewers.map(r => (
                            <span key={r.id} className="inline-flex items-center gap-1 text-[10px] font-sans text-trans-blue bg-trans-blue/5 border border-trans-blue/20 rounded-md px-2 py-0.5">
                              {r.name || r.email}
                              {(isAdmin() || isEditor()) && (
                                <button onClick={(e) => { e.stopPropagation(); handleRemoveReviewer(m.id, r.id); }} className="ml-0.5 hover:text-red-500 transition-colors" data-testid={`remove-reviewer-${r.id}`}>
                                  <X className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Consensus progress */}
                      {m.consensus && msReviewers.length > 1 && (
                        <div className="mt-2 flex items-center gap-2 text-[10px] font-sans">
                          <BarChart3 className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-500">
                            Dictamenes: {m.consensus.total_reviews}/{m.consensus.total_assigned}
                          </span>
                          {Object.entries(m.consensus.votes || {}).map(([k, v]) => (
                            <span key={k} className={`px-1.5 py-0.5 rounded ${k === 'Aceptado' ? 'bg-green-50 text-green-700' : k === 'Rechazado' ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'}`}>
                              {k}: {v}
                            </span>
                          ))}
                          {m.consensus.complete && <span className="text-green-600 font-medium">Completo</span>}
                        </div>
                      )}
                      {m.comments?.length > 0 && (
                        <div className="mt-2 p-2.5 bg-gray-50 rounded-sm border border-gray-100">
                          <p className="text-[10px] font-sans font-semibold text-gray-500 uppercase mb-1">Ultimo comentario</p>
                          <p className="text-xs font-sans text-gray-600">{m.comments[m.comments.length - 1].texto}</p>
                          <p className="text-[10px] font-sans text-gray-400 mt-1">— {m.comments[m.comments.length - 1].user_name}</p>
                        </div>
                      )}
                      {/* Deadline indicator */}
                      {m.review_deadline && (
                        <div className="mt-2 flex items-center gap-1.5 text-[10px] font-sans">
                          <CalendarClock className="w-3 h-3 text-gray-400" />
                          {(() => {
                            const dl = new Date(m.review_deadline);
                            const now = new Date();
                            const days = Math.ceil((dl - now) / (1000 * 60 * 60 * 24));
                            const dateStr = dl.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
                            if (days < 0) return <span className="text-red-500 font-medium">Plazo vencido ({dateStr})</span>;
                            if (days <= 3) return <span className="text-amber-500 font-medium">Vence en {days} dia{days !== 1 ? 's' : ''} ({dateStr})</span>;
                            return <span className="text-gray-500">Plazo: {dateStr}</span>;
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-1.5 mt-3 sm:mt-0 sm:flex-col sm:shrink-0">
                      {m.file_available && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="font-sans text-xs h-8"
                          disabled={downloading === m.id}
                          onClick={() => handleDownload(m)}
                          data-testid={`download-${i}`}
                        >
                          {downloading === m.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />} Descargar
                        </Button>
                      )}
                      {(isAdmin() || isEditor()) && !['Publicado', 'Rechazado'].includes(m.status) && (
                        <Button variant="outline" size="sm" className="font-sans text-xs h-8 text-trans-blue border-trans-blue/30" onClick={() => setAssignDialog(m)} data-testid={`assign-${i}`}>
                          <UserPlus className="w-3 h-3 mr-1" /> Asignar Revisore
                        </Button>
                      )}
                      {(isAdmin() || isEditor()) && !['Publicado', 'Rechazado'].includes(m.status) && (
                        <Button variant="outline" size="sm" className="font-sans text-xs h-8 text-amber-600 border-amber-200" onClick={() => { setDeadlineDialog(m); setDeadlineDate(m.review_deadline ? m.review_deadline.split('T')[0] : ''); }} data-testid={`deadline-${i}`}>
                          <CalendarClock className="w-3 h-3 mr-1" /> Plazo
                        </Button>
                      )}
                      {m.status !== 'Publicado' && m.status !== 'Rechazado' && (
                        <Button size="sm" className="font-sans text-xs h-8 bg-slate-dark" onClick={() => { setReviewDialog(m); setReviewForm({ decision: '', comentarios: '', comentarios_privados: '' }); }} data-testid={`review-${i}`}>
                          <Send className="w-3 h-3 mr-1" /> {isEditorial ? 'Decision editorial' : 'Dictaminar'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedMs} onOpenChange={() => setSelectedMs(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-lg">{selectedMs?.titulo}</DialogTitle></DialogHeader>
          {selectedMs && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-xs ${statusConfig[selectedMs.status]?.color || ''}`}>{selectedMs.status}</Badge>
                <span className="text-xs font-sans text-gray-400">{selectedMs.categoria}</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm font-sans">
                <div><span className="text-gray-400">Autore:</span> <span className="text-slate-dark font-medium">{selectedMs.autor_name}</span></div>
                <div><span className="text-gray-400">Email:</span> <span className="text-slate-dark">{selectedMs.autor_email}</span></div>
                <div><span className="text-gray-400">Fecha envio:</span> <span className="text-slate-dark">{new Date(selectedMs.created_at).toLocaleDateString('es-MX')}</span></div>
              </div>
              {/* Reviewers in detail */}
              <div>
                <span className="text-sm font-sans text-gray-400">Revisores asignados ({getReviewers(selectedMs).length}/5):</span>
                {getReviewers(selectedMs).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {getReviewers(selectedMs).map(r => (
                      <Badge key={r.id} variant="outline" className="text-xs font-sans text-trans-blue border-trans-blue/20">
                        {r.name} {r.assigned_at ? `(${new Date(r.assigned_at).toLocaleDateString('es-MX')})` : ''}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm font-sans text-gray-400 ml-1">Sin asignar</span>
                )}
              </div>
              {/* Consensus info */}
              {selectedMs.consensus && getReviewers(selectedMs).length > 1 && (
                <div className="p-3 bg-gray-50 rounded-sm border border-gray-100">
                  <p className="text-xs font-sans font-semibold text-gray-500 uppercase mb-2">Consenso de Revision</p>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-sans text-slate-dark font-medium">
                      {selectedMs.consensus.total_reviews}/{selectedMs.consensus.total_assigned} dictamenes
                    </span>
                    {selectedMs.consensus.complete && <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]">Completo</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedMs.consensus.votes || {}).map(([k, v]) => (
                      <div key={k} className={`px-3 py-1.5 rounded-sm border text-xs font-sans font-medium ${k === 'Aceptado' ? 'bg-green-50 text-green-700 border-green-200' : k === 'Rechazado' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                        {k}: {v} voto{v !== 1 ? 's' : ''}
                      </div>
                    ))}
                  </div>
                  {selectedMs.consensus.consensus_status && (
                    <p className="text-xs font-sans text-gray-500 mt-2">
                      Decision por consenso: <strong>{selectedMs.consensus.consensus_status}</strong>
                    </p>
                  )}
                </div>
              )}
              {selectedMs.palabras_clave?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedMs.palabras_clave.map(k => <span key={k} className="text-[10px] font-sans px-2 py-0.5 bg-gray-50 rounded text-gray-500">{k}</span>)}
                </div>
              )}
              <div>
                <h4 className="text-sm font-sans font-semibold text-slate-dark mb-1">Resumen</h4>
                <p className="text-sm font-serif text-gray-600 leading-relaxed">{selectedMs.abstract_es}</p>
              </div>
              {selectedMs.abstract_en && (
                <div>
                  <h4 className="text-sm font-sans font-semibold text-slate-dark mb-1">Abstract</h4>
                  <p className="text-sm font-serif text-gray-500 italic leading-relaxed">{selectedMs.abstract_en}</p>
                </div>
              )}
              {selectedMs.comments?.length > 0 && (
                <div>
                  <h4 className="text-sm font-sans font-semibold text-slate-dark mb-2">Historial de Comentarios</h4>
                  <div className="space-y-2">
                    {selectedMs.comments.map(c => (
                      <div key={c.id} className="p-3 bg-gray-50 rounded-sm border border-gray-100">
                        <p className="text-xs font-sans text-gray-600">{c.texto}</p>
                        <p className="text-[10px] font-sans text-gray-400 mt-1">— {c.user_name} &middot; {new Date(c.created_at).toLocaleDateString('es-MX')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Reviewer Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => { setAssignDialog(null); setAssignReviewerId(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Asignar Revisore</DialogTitle></DialogHeader>
          {assignDialog && (
            <>
              <p className="text-sm font-sans text-gray-500 mb-1">Manuscrito: <strong>{assignDialog.titulo}</strong></p>
              {/* Currently assigned reviewers */}
              {getReviewers(assignDialog).length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-sans text-gray-400 mb-1.5">Revisores actuales:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {getReviewers(assignDialog).map(r => (
                      <Badge key={r.id} variant="outline" className="text-xs font-sans text-trans-blue border-trans-blue/20">{r.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs font-sans text-gray-400 mb-2">Selecciona une revisore adicional:</p>
              <Select onValueChange={setAssignReviewerId} data-testid="select-reviewer">
                <SelectTrigger className="font-sans"><SelectValue placeholder="Seleccionar revisore..." /></SelectTrigger>
                <SelectContent>
                  {getAvailableReviewers(assignDialog).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name} {r.apellidos} — {r.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getAvailableReviewers(assignDialog).length === 0 && (
                <p className="text-xs font-sans text-gray-400 mt-1">Todos los revisores ya estan asignados.</p>
              )}
              <Button onClick={handleAssign} className="w-full bg-slate-dark font-sans mt-2" disabled={!assignReviewerId} data-testid="confirm-assign-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Asignar
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif">{isEditorial ? 'Decision Editorial' : 'Enviar Dictamen'}</DialogTitle></DialogHeader>
          <p className="text-xs font-sans text-gray-400 mb-2">{reviewDialog?.titulo}</p>
          <div className="space-y-4">
            {isEditorial && (
              <p className="text-xs font-sans text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="editorial-note">
                Como parte del equipo editorial tu decision es final y se aplica de inmediato, sin esperar el consenso de revisores.
                {reviewDialog?.consensus?.total_reviews > 0 && (
                  <> Hasta ahora: {reviewDialog.consensus.total_reviews}/{reviewDialog.consensus.total_assigned} dictamenes de revisores.</>
                )}
              </p>
            )}
            <div>
              <label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Decision *</label>
              <Select value={reviewForm.decision} onValueChange={v => setReviewForm(f => ({ ...f, decision: v }))}>
                <SelectTrigger className="font-sans" data-testid="review-decision"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aceptar" data-testid="decision-aceptar"><span className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-500" /> Aceptar</span></SelectItem>
                  <SelectItem value="Revisiones Requeridas" data-testid="decision-revisiones"><span className="flex items-center gap-2"><AlertCircle className="w-3 h-3 text-orange-500" /> Revisiones Requeridas</span></SelectItem>
                  <SelectItem value="Rechazar" data-testid="decision-rechazar"><span className="flex items-center gap-2"><XCircle className="w-3 h-3 text-red-500" /> Rechazar</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Comentarios para le autore *</label>
              <Textarea value={reviewForm.comentarios} onChange={e => setReviewForm(f => ({ ...f, comentarios: e.target.value }))} rows={4} className="font-sans" placeholder="Evaluacion del manuscrito..." data-testid="review-comments" />
            </div>
            <div>
              <label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Notas privadas (solo equipo editorial)</label>
              <Textarea value={reviewForm.comentarios_privados} onChange={e => setReviewForm(f => ({ ...f, comentarios_privados: e.target.value }))} rows={2} className="font-sans" placeholder="Notas internas..." data-testid="review-private" />
            </div>
            <Button onClick={handleReview} className="w-full bg-slate-dark font-sans" disabled={!reviewForm.decision || !reviewForm.comentarios} data-testid="submit-review-btn">
              <Send className="w-4 h-4 mr-2" /> Enviar Dictamen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deadline dialog */}
      <Dialog open={!!deadlineDialog} onOpenChange={() => setDeadlineDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-serif">Establecer Plazo de Revision</DialogTitle></DialogHeader>
          {deadlineDialog && (
            <div className="space-y-4">
              <p className="text-sm font-sans text-gray-500">
                Manuscrito: <strong>{deadlineDialog.titulo}</strong>
              </p>
              {deadlineDialog.review_deadline && (
                <p className="text-xs font-sans text-gray-400">
                  Plazo actual: {new Date(deadlineDialog.review_deadline).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
              <div>
                <label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Nueva fecha limite</label>
                <Input type="date" value={deadlineDate} onChange={e => setDeadlineDate(e.target.value)} className="font-sans" data-testid="deadline-date-input" min={new Date().toISOString().split('T')[0]} />
              </div>
              <Button onClick={handleSetDeadline} className="w-full bg-slate-dark font-sans" disabled={!deadlineDate} data-testid="confirm-deadline-btn">
                <CalendarClock className="w-4 h-4 mr-2" /> {deadlineDialog.review_deadline ? 'Extender Plazo' : 'Establecer Plazo'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
